import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ContactSection = styled.section`
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  background: #e3e3dd;
  color: var(--navy-blue);
  font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* Overlay crema más opaco para legibilidad sobre el video */
    background: linear-gradient(
      135deg,
      rgba(227, 227, 221, 0.62) 0%,
      rgba(227, 227, 221, 0.56) 50%,
      rgba(227, 227, 221, 0.62) 100%
    );
    z-index: 1;
    pointer-events: none;
  }
`;

const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Overscan para eliminar franjas laterales del propio video o sub-pixel gaps */
  transform: scale(1.08);
  object-fit: cover;
  object-position: center;
  z-index: 0;
  opacity: 0.48;

  /* Solo web/desktop (mejor performance en móvil) */
  @media (max-width: 768px) {
    display: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  color: var(--navy-blue);
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  .highlight {
    color: var(--navy-blue);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(1, 74, 144, 0.85);
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div``;

const InfoTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: var(--navy-blue);
  margin-bottom: 2rem;
  
  .highlight {
    color: var(--navy-blue);
  }
`;

const InfoText = styled.p`
  color: rgba(1, 74, 144, 0.85);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 3rem;
  opacity: 0.9;
`;

const ContactMethods = styled.div`
  margin-bottom: 3rem;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(227, 227, 221, 0.85);
  border-radius: 15px;
  border: 1px solid rgba(139, 69, 19, 0.2);
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: rgba(227, 227, 221, 0.95);
    border-color: var(--brand-gold);
    transform: translateX(10px);
    box-shadow: 0 8px 25px rgba(139, 69, 19, 0.2);
  }
`;

const MethodIcon = styled.div`
  width: 50px;
  height: 50px;
  background: var(--gradient-gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.2rem;
  color: var(--navy-blue);
  box-shadow: 0 3px 10px rgba(211, 141, 84, 0.3);
`;

const MethodContent = styled.div``;

const MethodTitle = styled.h4`
  color: var(--navy-blue);
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const MethodValue = styled.p`
  color: rgba(1, 74, 144, 0.85);
  font-size: 0.95rem;
  font-weight: 500;
`;

const SocialSection = styled.div``;

const SocialTitle = styled.h4`
  color: var(--navy-blue);
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
`;

const SocialLink = styled.a`
  width: 60px;
  height: 60px;
  background: rgba(58, 174, 171, 0.10);
  border: 2px solid var(--primary-turquoise);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--primary-turquoise);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    width: 100%;
    height: 100%;
    background: var(--primary-turquoise);
    border-radius: 50%;
    transition: transform 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    color: var(--navy-blue);
    transform: translateY(-5px) scale(1.1);
    
    &::before {
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  &.facebook:hover { border-color: #1877f2; }
  &.instagram:hover { border-color: #e4405f; }
  &.tiktok:hover { border-color: #000000; }
`;

const ContactForm = styled.div``;

const FormTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: var(--navy-blue);
  margin-bottom: 1rem;
  text-align: center;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
  
  .highlight {
    color: var(--primary-turquoise);
    -webkit-text-stroke: 0px transparent;
    text-shadow: none;
  }
`;

const FormSubtitle = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const Form = styled.form`
  background: rgba(227, 227, 221, 0.92);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(139, 69, 19, 0.2);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: var(--navy-blue);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  background: rgba(227, 227, 221, 0.85);
  border: 2px solid rgba(211, 141, 84, 0.35);
  border-radius: 10px;
  color: var(--navy-blue);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(102, 102, 102, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    background: rgba(227, 227, 221, 0.95);
    box-shadow: 0 0 20px rgba(211, 141, 84, 0.18);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  background: rgba(227, 227, 221, 0.85);
  border: 2px solid rgba(211, 141, 84, 0.35);
  border-radius: 10px;
  color: var(--navy-blue);
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(102, 102, 102, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    background: rgba(227, 227, 221, 0.95);
    box-shadow: 0 0 20px rgba(211, 141, 84, 0.18);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: var(--gradient-gold);
  color: var(--navy-blue);
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(139, 69, 19, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0);
    width: 100%;
    height: 100%;
    background: rgba(227, 227, 221, 0.2);
    border-radius: 25px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(139, 69, 19, 0.4);
    
    &::before {
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
  
  i {
    margin-right: 10px;
  }
`;

const SuccessMessage = styled.div<{ show: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  background: var(--gradient-primary);
  color: var(--navy-blue);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  gap: 10px;
  z-index: 1000;
  animation: slideInRight 0.5s ease;
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const Contact: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // GSAP animations
    gsap.fromTo('.section-header',
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%',
          end: 'bottom 30%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    gsap.fromTo('.contact-info',
      { opacity: 0, x: -50 },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        scrollTrigger: {
          trigger: '.contact-grid',
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    gsap.fromTo('.contact-form',
      { opacity: 0, x: 50 },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        delay: 0.2,
        scrollTrigger: {
          trigger: '.contact-grid',
          start: 'top 80%',
          end: 'bottom 20%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    gsap.fromTo('.contact-method',
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        scrollTrigger: {
          trigger: '.contact-methods',
          start: 'top 90%',
          end: 'bottom 10%',
          toggleActions: 'play none none reverse'
        }
      }
    );

    gsap.fromTo('.social-link',
      { opacity: 0, scale: 0 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        stagger: 0.1,
        delay: 0.5,
        scrollTrigger: {
          trigger: '.social-links',
          start: 'top 90%',
          end: 'bottom 10%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }, 2000);
  };

  return (
    <ContactSection id="contacto" ref={sectionRef}>
      <BackgroundVideo autoPlay muted loop playsInline>
        <source src={`${process.env.PUBLIC_URL}/video.mp4`} type="video/mp4" />
        Tu navegador no soporta el elemento video.
      </BackgroundVideo>
      <Container>
        <SectionHeader className="section-header">
          <SectionTitle>
            <span className="highlight">Contáctanos</span>
          </SectionTitle>
          <SectionSubtitle>
            Estamos aquí para servirte. ¡Haz tu reserva o cuéntanos tu experiencia!
          </SectionSubtitle>
        </SectionHeader>

        <ContactGrid className="contact-grid">
          <ContactInfo className="contact-info">
            <InfoTitle>
              ¡Hablemos de <span className="highlight">Mariscos</span>!
            </InfoTitle>
            <InfoText>
              Nos encanta escuchar de nuestros clientes. Ya sea para una reserva especial, 
              comentarios sobre tu experiencia, o simplemente para conocer más sobre 
              nuestras especialidades marinas.
            </InfoText>

            <ContactMethods className="contact-methods">
              <ContactMethod className="contact-method">
                <MethodIcon>
                  <i className="fas fa-envelope"></i>
                </MethodIcon>
                <MethodContent>
                  <MethodTitle>Correo Electrónico</MethodTitle>
                  <MethodValue>contacto@pepetiburon.com</MethodValue>
                </MethodContent>
              </ContactMethod>

              <ContactMethod className="contact-method">
                <MethodIcon>
                  <i className="fas fa-clock"></i>
                </MethodIcon>
                <MethodContent>
                  <MethodTitle>Horario de Atención</MethodTitle>
                  <MethodValue>Lun - Dom: 10:00 AM - 5:00 PM</MethodValue>
                </MethodContent>
              </ContactMethod>
            </ContactMethods>

            <SocialSection>
              <SocialTitle>Síguenos en Redes Sociales</SocialTitle>
              <SocialLinks className="social-links">
                <SocialLink 
                  href="https://web.facebook.com/pepetiburon01/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link facebook"
                >
                  <i className="fab fa-facebook-f"></i>
                </SocialLink>
                <SocialLink 
                  href="https://instagram.com/pepe.tiburon" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link instagram"
                >
                  <i className="fab fa-instagram"></i>
                </SocialLink>
                <SocialLink 
                  href="https://tiktok.com/@cevicheriapepe_tiburon" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link tiktok"
                >
                  <i className="fab fa-tiktok"></i>
                </SocialLink>
              </SocialLinks>
            </SocialSection>
          </ContactInfo>

          <ContactForm className="contact-form">
            <FormTitle>
              Envíanos un <span className="highlight">Mensaje</span>
            </FormTitle>
            <FormSubtitle>
              Completa el formulario y te responderemos a la brevedad
            </FormSubtitle>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Nombre Completo</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Tu nombre completo"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+51 999 999 999"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Asunto</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="¿Sobre qué quieres hablarnos?"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Mensaje</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Cuéntanos tu experiencia, sugerencia o consulta..."
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i>
                    Enviar Mensaje
                  </>
                )}
              </SubmitButton>
            </Form>
          </ContactForm>
        </ContactGrid>
      </Container>

      <SuccessMessage show={showSuccess}>
        <i className="fas fa-check-circle"></i>
        ¡Mensaje enviado exitosamente! Te responderemos pronto.
      </SuccessMessage>
    </ContactSection>
  );
};

export default Contact;